
import type { UserAPIInterface } from './api-switcher'
import type { components } from './auto-gen-types'
import { client } from './client'
import { responseDataConverter, apiUserToUser, apiMeToMe } from './converters'

// Type aliases for invitation and join request responses
type AlbumInvitation = components['schemas']['AlbumInvitation']
type AlbumJoinRequest = components['schemas']['AlbumJoinRequest']

export const UserAPI: UserAPIInterface = {
  getMe: (signal) =>
    responseDataConverter(client.GET('/me', { signal }), apiMeToMe),

  getStorageBreakdown: (signal) =>
    client.GET('/me/storage-breakdown', { signal }),

  getUser: (user_id, signal) =>
    responseDataConverter(
      client.GET('/user/{UserId}', {
        params: {
          path: { UserId: user_id }
        },
        signal
      }),
      apiUserToUser
    ),

  getUserIdFromMetamoryId: (metamory_id, signal) =>
    client.GET('/search/user/by_id', {
      params: {
        query: { id: metamory_id },
      },
      signal
    }),

  updateMe: (body: {
    user_id: string,
    metamory_id: string,
    user_name: string,
    user_icon: string,
    description: string,
    twitter_id?: string | undefined,
    discord_id?: string | undefined,
    vrchat_user_id?: string | undefined,
    vrchat_display_name?: string | undefined,
  }, signal?: AbortSignal) =>
    responseDataConverter(
      client.PUT('/me', {
        // @ts-expect-error body type mismatch with OpenAPI spec
        body,
        signal
      }),
      apiMeToMe
    ),

  uploadMeIcon: (file, width, height, last_modified) =>
    responseDataConverter(
      client.POST('/me/icon', {
        body: {
          // @ts-expect-error string type specified but File is required
          file: file,
          width, height, last_modified
        },
        bodySerializer: (body: Record<string, unknown> | undefined) => {
          const formData = new FormData()
          for (const [key, value] of Object.entries(body!)) {
            if (value instanceof File) formData.append(key, value)
            else formData.append(key, String(value))
          }
          return formData
        }
      }),
      () => undefined // Return type differs from spec
    ),

  deleteMe: () =>
    responseDataConverter(client.DELETE('/me'), () => undefined),

  markHintSeen: (hint_key) =>
    client.POST('/me/hints/{hint_key}', { params: { path: { hint_key } } }),

  acceptedRules: () =>
    client.POST('/acceptRules').then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  muteUser: (target_user_id, mute) =>
    responseDataConverter(
      mute
        ? client.POST('/mute', { body: { target_user_id } })
        : client.DELETE('/mute', { body: { target_user_id } }),
      () => undefined
    ),

  searchAllUsers: (name, signal) =>
    client.GET('/search/user/by_name', {
      params: { query: { name } },
      signal
    })
    .then(({ data, error, response }) => {
      if (error) return { error, response }
      return {
        data: data?.users?.map(apiUserToUser) || [],
        response
      }
    }),

  // TODO: Implement proper address book API endpoint
  listBookUsers: () =>
    Promise.resolve({
      error: { code: 0, message: 'Not implemented' },
      response: new Response()
    }),

  listMutedUsers: (signal?: AbortSignal) =>
    client.GET('/mute', { signal })
    .then(({ data, error, response }) => {
      if (error) return { error, response }
      return {
        data: data?.users?.map(apiUserToUser) || [],
        response
      }
    }),

  // TODO: Implement photo suggestions API endpoint
  searchPhotoSuggestions: () =>
    Promise.resolve({
      error: { code: 0, message: 'Not implemented' },
      response: new Response()
    }),

  // TODO: Implement group suggestions API endpoint
  groupSuggestionUsers: () =>
    Promise.resolve({
      error: { code: 0, message: 'Not implemented' },
      response: new Response()
    }),

  // TODO: Implement book suggestions API endpoint
  bookSuggestionUsers: () =>
    Promise.resolve({
      error: { code: 0, message: 'Not implemented' },
      response: new Response()
    }),

  // TODO: Implement tag suggestions API endpoint
  tagSuggestionUsers: () =>
    Promise.resolve({
      data: [],
      response: new Response()
    }),

  // TODO: Implement book users update API endpoint
  bookUsers: () =>
    Promise.resolve({
      error: { code: 0, message: 'Not implemented' },
      response: new Response()
    }),

  updateBirthDate: (birth_date: Date) =>
    client.PUT('/me/birth-date', {
      body: {
        // Format from local calendar parts — toISOString() would shift the
        // date across the UTC boundary for JST-midnight Date values.
        birth_date: [
          birth_date.getFullYear(),
          String(birth_date.getMonth() + 1).padStart(2, '0'),
          String(birth_date.getDate()).padStart(2, '0')
        ].join('-')
      }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  updateGlobalSensitiveFilter: (enabled: boolean) =>
    client.PUT('/me/sensitive-filter', {
      body: { enabled }
    }).then(({ error, response }) =>
      error
        ? { error: { code: error.code || 0, message: error.message || 'Unknown error' }, response }
        : { response }
    ),

  sendCampaignCodes: (campaign_codes: string[]) => {
    // Apply each promotion code individually
    return Promise.all(
      campaign_codes.map(code =>
        client.POST('/promotions/apply', {
          body: { code }
        })
      )
    ).then(results => {
      // Combine results to match the expected return format
      const successful_results = results
        .map(result => {
          if (result.data && result.data.success) {
            // Map promotion types to campaign IDs for backward compatibility
            const campaign_id = result.data.type === 'count_free' && result.data.promotion_id.includes('vket2025s')
              ? 'vket2025s'
              : result.data.promotion_id
            return { campaign_id }
          }
          return undefined
        })
        .filter((r): r is { campaign_id: string } => r !== undefined)

      // Return in the expected format matching OpenAPIResponse type
      const hasError = results.some(r => r.error)
      if (hasError) {
        // Find the first error and ensure it's not undefined
        const errorResult = results.find(r => r.error)
        return {
          error: errorResult?.error || { code: 500, message: 'Unknown error' },
          response: errorResult?.response || new Response()
        }
      }

      return {
        data: successful_results,
        response: results[0]?.response || new Response()
      }
    })
  },

  // Invitation and join request methods
  listMyInvitations: (signal) =>
    responseDataConverter(
      client.GET('/me/invitations', { signal }),
      (data) => data.invitations.map((inv: AlbumInvitation) => ({
        share_id: inv.share_id,
        share_name: inv.share_name,
        share_thumbnail_url: inv.share_thumbnail_url || '',
        owner: apiUserToUser(inv.owner),
        invited_by: apiUserToUser(inv.invited_by),
        invited_at: new Date(inv.invited_at)
      }))
    ),

  listMyJoinRequests: (signal) =>
    responseDataConverter(
      client.GET('/me/join-requests', { signal }),
      (data) => data.join_requests.map((req: AlbumJoinRequest) => ({
        share_id: req.share_id,
        share_name: req.share_name,
        share_thumbnail_url: req.share_thumbnail_url || '',
        owner: apiUserToUser(req.owner),
        requested_at: new Date(req.requested_at)
      }))
    ),
}
